/**
 * DurationInput — a 24-hour HH:MM duration field with two clickable segments
 * (hours / minutes). No AM/PM, hours-first. Click either segment to edit just
 * that part; typing auto-advances hours → minutes with native-style clamping
 * (utils/durationClock). Controlled by `value` as the 4-char "HHMM" digit
 * string the rest of the app uses (parseDuration / BE); `onValueChange` emits
 * the same.
 */

import * as React from "react";
import styled from "styled-components";
import { theme } from "styles/theme";
import {
  partsFromValue,
  partsToDigits,
  padSegment,
  typeHours,
  typeMinutes,
} from "utils/durationClock";

type Props = {
  name?: string;
  id?: string;
  /** 4-char "HHMM" digit string (e.g. parseDuration(min).fullStr), or "". */
  value?: string;
  /** Emits the 4-char "HHMM" digit string ("" when empty). */
  onValueChange?: (digits: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  className?: string;
};

const Box = styled.div<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${theme.border.light};
  border-radius: 5px;
  background: ${(p) => (p.$disabled ? theme.bg.blue : "white")};
  height: ${theme.height.regular};
  padding: 0 1em;
  width: 100%;
  font-size: ${theme.fontSize.regular};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  transition: all 0.09s linear;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "text")};
  &:hover {
    border-color: ${(p) => (p.$disabled ? theme.border.light : "#5d6d7e")};
  }
  &:focus-within {
    border-color: #3498db;
  }
`;

const Seg = styled.input`
  border: none;
  outline: none;
  background: transparent;
  width: 2ch;
  text-align: center;
  font: inherit;
  color: inherit;
  padding: 0;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const Colon = styled.span`
  padding: 0 0.15em;
  user-select: none;
`;

const ALLOWED = ["Tab", "ArrowUp", "ArrowDown", "Enter", "Escape"];

export default function DurationInput({
  value = "",
  onValueChange,
  onBlur,
  onKeyDown,
  disabled,
  className,
  name,
  id,
}: Props) {
  const initial = partsFromValue(value);
  const [hh, setHh] = React.useState(initial.hh);
  const [mm, setMm] = React.useState(initial.mm);

  const hhRef = React.useRef<HTMLInputElement>(null);
  const mmRef = React.useRef<HTMLInputElement>(null);
  const boxRef = React.useRef<HTMLDivElement>(null);
  const lastEmitted = React.useRef<string>(partsToDigits(initial));
  // When a segment was just focused, the next digit starts that segment fresh.
  const freshHh = React.useRef(false);
  const freshMm = React.useRef(false);

  // Sync from an externally-changed value (reset / type select), but never
  // clobber what we just emitted (which the parent echoes back).
  React.useEffect(() => {
    const incoming = (value ?? "").replace(/[^0-9]/g, "");
    if (incoming !== lastEmitted.current) {
      const next = partsFromValue(value);
      setHh(next.hh);
      setMm(next.mm);
      lastEmitted.current = partsToDigits(next);
    }
  }, [value]);

  const emit = (nextHh: string, nextMm: string) => {
    setHh(nextHh);
    setMm(nextMm);
    const digits = partsToDigits({ hh: nextHh, mm: nextMm });
    lastEmitted.current = digits;
    onValueChange?.(digits);
  };

  const focusMinutes = () => {
    freshMm.current = true;
    mmRef.current?.focus();
    mmRef.current?.select();
  };

  const handleHoursKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const base = freshHh.current ? "" : hh;
      freshHh.current = false;
      const r = typeHours(base, e.key);
      if (r.overflow != null) {
        const m = typeMinutes("", r.overflow);
        emit(r.hh, m.mm);
        focusMinutes();
        freshMm.current = false; // the overflow digit already seeded minutes
      } else {
        emit(r.hh, mm);
        if (r.advance) focusMinutes();
      }
      return;
    }
    if (e.key === "Backspace") {
      e.preventDefault();
      freshHh.current = false;
      emit(hh.slice(0, -1), mm);
      return;
    }
    if (e.key === "ArrowRight" || e.key === ":") {
      e.preventDefault();
      focusMinutes();
      return;
    }
    if (!ALLOWED.includes(e.key) && !e.metaKey && !e.ctrlKey && e.key !== "ArrowLeft") {
      e.preventDefault();
    }
  };

  const handleMinutesKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const base = freshMm.current ? "" : mm;
      freshMm.current = false;
      emit(hh, typeMinutes(base, e.key).mm);
      return;
    }
    if (e.key === "Backspace") {
      e.preventDefault();
      if (mm.length > 0) {
        emit(hh, mm.slice(0, -1));
      } else {
        freshHh.current = false;
        hhRef.current?.focus();
        hhRef.current?.select();
      }
      return;
    }
    if (e.key === "ArrowLeft") {
      const atStart = mmRef.current?.selectionStart === 0;
      if (atStart) {
        e.preventDefault();
        freshHh.current = true;
        hhRef.current?.focus();
        hhRef.current?.select();
      }
      return;
    }
    if (!ALLOWED.includes(e.key) && !e.metaKey && !e.ctrlKey && e.key !== "ArrowRight") {
      e.preventDefault();
    }
  };

  // Clicking the box chrome (padding / colon), not a segment, focuses hours.
  const handleBoxMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.target !== hhRef.current && e.target !== mmRef.current) {
      e.preventDefault();
      freshHh.current = true;
      hhRef.current?.focus();
      hhRef.current?.select();
    }
  };

  // Normalize single-digit segments to two digits when leaving the whole field.
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (boxRef.current?.contains(e.relatedTarget as Node)) return; // moving between segments
    const ph = hh ? padSegment(hh) : "";
    const pm = mm ? padSegment(mm) : "";
    if (ph !== hh || pm !== mm) emit(ph, pm);
    onBlur?.(e);
  };

  return (
    <Box
      ref={boxRef}
      $disabled={disabled}
      className={className}
      onMouseDown={handleBoxMouseDown}
      onBlur={handleBlur}
    >
      <Seg
        ref={hhRef}
        type="text"
        inputMode="numeric"
        name={name}
        id={id}
        disabled={disabled}
        aria-label="Hours"
        placeholder="00"
        value={hh}
        onChange={() => {}}
        onKeyDown={handleHoursKey}
        onFocus={() => {
          freshHh.current = true;
          hhRef.current?.select();
        }}
      />
      <Colon>:</Colon>
      <Seg
        ref={mmRef}
        type="text"
        inputMode="numeric"
        disabled={disabled}
        aria-label="Minutes"
        placeholder="00"
        value={mm}
        onChange={() => {}}
        onKeyDown={handleMinutesKey}
        onFocus={() => {
          freshMm.current = true;
          mmRef.current?.select();
        }}
      />
    </Box>
  );
}
